import Image, { getImageProps } from 'next/image';
import cn from 'classnames';

import Heading from '@components/ui/Heading';
import LinkButton from '_components/UI/LinkButton';
import HeroInfoCard from '_components/HeroInfoCard';
import { HeroInfoCardData } from '_components/HeroInfoCard/types';
import { Breakpoints } from '@hooks/useBreakpoint';
import { useLocale } from '@hooks/useLocale';
import { localizeHref } from '@utils/localizeHref';
import {
  INFO_CARD_CTA_SLOT,
  heroCtaHandlers,
} from '_utils/GA4/heroCtaHandlers';
import { CAMPAIGN_HERO_TYPE } from '_utils/GA4/trackMainHeroEvents';
import { CampaignHeroData } from './types';

import styles from './CampaignHero.module.scss';

// Campaign hero slices have no co-op brand field.
const TRACKING = { type: CAMPAIGN_HERO_TYPE, coOpBrand: null };

export interface CampaignHeroProps {
  data: CampaignHeroData;
  priority?: boolean;
}

const CampaignHeroPicture = ({
  desktopSrc,
  mobileSrc,
  alt,
  sizes,
  priority,
}: {
  desktopSrc: string;
  mobileSrc: string;
  alt: string;
  sizes: string;
  priority: boolean;
}) => {
  const loadingProps = priority
    ? ({ loading: 'eager', fetchPriority: 'high' } as const)
    : {};
  const { props: desktopImageProps } = getImageProps({
    src: desktopSrc,
    alt,
    fill: true,
    sizes,
  });
  const { props: mobileImageProps } = getImageProps({
    src: mobileSrc,
    alt,
    fill: true,
    sizes,
    className: styles.image,
    ...loadingProps,
  });

  return (
    <picture>
      <source
        media={`(min-width: ${Breakpoints.md}px)`}
        srcSet={desktopImageProps.srcSet ?? desktopImageProps.src}
        sizes={desktopImageProps.sizes}
      />
      <img {...mobileImageProps} />
    </picture>
  );
};

const CampaignHeroInfoCard = ({ card }: { card: HeroInfoCardData }) => {
  const locale = useLocale();
  const href = localizeHref(card.cta.url, locale);
  return (
    <HeroInfoCard
      {...card}
      href={href}
      className={styles.infoCard}
      {...heroCtaHandlers(TRACKING, href, card.cta.label, INFO_CARD_CTA_SLOT)}
    />
  );
};

const CampaignHero = ({ data, priority = false }: CampaignHeroProps) => {
  const {
    h1,
    subheading,
    layout,
    flipped,
    image,
    mobileImage,
    ctas,
    infoCard,
  } = data;
  const locale = useLocale();

  if (!h1) return null;

  const sideBySide = layout === 'sideBySide';
  const sizes = sideBySide ? '(min-width: 720px) 50vw, 100vw' : '100vw';

  return (
    <section
      className={cn(
        styles.hero,
        sideBySide && styles.sideBySide,
        flipped && styles.flipped
      )}
      data-layout={layout}
      data-testid="campaign-hero"
    >
      <div className={styles.content}>
        <div className={styles.copy}>
          <Heading semanticLevel="h1" styleLevel="1" className={styles.title}>
            {h1}
          </Heading>
          {subheading && <p className={styles.subheading}>{subheading}</p>}
        </div>
        {ctas.length > 0 && (
          <div className={styles.ctaRow}>
            {ctas.map(({ label, url, slot }) => {
              const href = localizeHref(url, locale);
              return (
                <LinkButton
                  key={`${url}-${slot}`}
                  href={href}
                  variant="black"
                  className={styles.cta}
                  {...heroCtaHandlers(TRACKING, href, label, slot)}
                >
                  {label}
                </LinkButton>
              );
            })}
          </div>
        )}
      </div>
      {image?.url && (
        <div className={styles.media}>
          {mobileImage?.url ? (
            <CampaignHeroPicture
              desktopSrc={image.url}
              mobileSrc={mobileImage.url}
              alt={image.alt || mobileImage.alt || ''}
              sizes={sizes}
              priority={priority}
            />
          ) : (
            <Image
              src={image.url}
              alt={image.alt ?? ''}
              fill
              priority={priority}
              sizes={sizes}
              className={styles.image}
            />
          )}
          <div className={styles.overlay} aria-hidden="true" />
          {infoCard && <CampaignHeroInfoCard card={infoCard} />}
        </div>
      )}
    </section>
  );
};

export default CampaignHero;
